import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useWindowDimensions, View } from "@-/com.npmjs.react-native";
import Animated, {
  Easing,
  FadeIn,
  FadeOut,
  SlideOutDown,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import {
  ONBOARDING_ANNOUNCEMENTS,
  ONBOARDING_INTRODUCTIONS,
  type OnboardingTextPage,
} from "@-/dev.improve.haniverse.assets.onboarding";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { playOnboardingReaction } from "@-/dev.improve.haniverse.lib.onboarding";
import type { OnboardingPageReaction } from "@-/dev.improve.haniverse.lib.onboarding.state";
import {
  PRIMARY_NAVIGATION_STRIP_FADE_INSET_PX,
  getPrimaryNavigationBarHeight,
  getPrimaryNavigationStripSurfaceClassName,
  usePrimaryNavigationStripSurfaceColorVariable,
} from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { boundedPromise } from "@-/dev.improve.haniverse.lib.deadline";
import { OnboardingContinueButton } from "#onboarding-intro-controls";
import { HaniSheetPerch } from "@-/dev.improve.haniverse.components.ui.hani-sheet-perch";
import type { OnboardingSheetGate } from "#use-onboarding-sheet-gate";
import { OnboardingSpeechBubble } from "#onboarding-speech-bubble";

/**
 * The sheet's raised, rounded surface, as the map's own sheet draws it. It is filled with the
 * strip's own colour (`getPrimaryNavigationStripSurfaceClassName`), so the two read as one surface.
 * Unlike the map's sheet it has no drag handle: the onboarding cannot be dismissed, only continued.
 */
// oxlint-disable-next-line tailwindcss/no-restricted-classes -- Sheets use platform-style 28pt exposed corners.
const SHEET_SURFACE_CLASS_NAME =
  "rounded-t-[28px] px-5 pt-10 shadow-[0_-2px_16px_rgba(0,0,0,0.16)]";

/**
 * Room above the sheet for its shadow. Resting on the strip, the sheet clips the rest of its
 * shadow, which would otherwise fall across the strip and part the two.
 */
const SHEET_SHADOW_ROOM_CLASS_NAME = "overflow-hidden pt-5";

/**
 * The strip's solid fill starts this far below its top: the gutter above its icons belongs to the
 * scene's fade (`PrimaryNavigationStrip`), which would show the page through a band between the
 * sheet and the strip. The sheet reaches down over it, so its surface meets the strip's fill.
 */
const STRIP_FADE_GUTTER_HEIGHT = PRIMARY_NAVIGATION_STRIP_FADE_INSET_PX;

/** In landscape the strip is a rail at the side, and the sheet sits at the bottom, this wide. */
const LANDSCAPE_SHEET_MAX_WIDTH = 460;

/**
 * The onboarding's sheet, embedded just above the strip. It reacts to where the learner is: the
 * first time they open a page it introduces it, one page of the sheet at a time, and the
 * introduction's last Continue unlocks the next destination, which the sheet then announces with a
 * "Go to" button that switches tabs as the strip's own buttons do (`switchTab`). Walked away from,
 * an introduction waits for the learner's next visit, and an announcement is not shown again.
 * What it says is authored in `assets/onboarding`'s `introductions` and `announcements` files.
 */
export function OnboardingSheet({
  gate,
  switchTab,
}: {
  gate: OnboardingSheetGate;
  /** Switch to a tab by screen name, as a tap on its strip button does. */
  switchTab: (screenName: string) => void;
}) {
  const { subject } = gate;
  const insets = useSafeAreaInsets();
  const { height, width } = useWindowDimensions();
  const isLandscape = width > height;
  const progress = useSharedValue(0);
  const releaseEntrance = useRef<() => void>(() => {});
  const handleHaniReady = useCallback(() => releaseEntrance.current(), []);
  useEffect(() => {
    let active = true;
    // A bundled image is ready before the shared entrance starts. Asset failures fail open.
    void boundedPromise(
      { deadline: 1500, onDeadline: "resolve", operation: "Prepare onboarding sheet artwork" },
      (resolve) => {
        releaseEntrance.current = resolve;
      },
    ).then(() => {
      if (active) {
        progress.set(withTiming(1, { duration: 500, easing: Easing.out(Easing.cubic) }));
      }
    });
    return () => {
      active = false;
      releaseEntrance.current();
      cancelAnimation(progress);
    };
  }, [progress]);
  const entranceStyle = useAnimatedStyle(() => ({
    opacity: progress.get() === 0 ? 0 : 1,
    transform: [{ translateY: (1 - progress.get()) * height }],
  }));
  const [androidUsesButtonNavBar] = useContextSelectors(
    NavContext,
    (context) => context.androidUsesButtonNavBar,
  );
  const stripHeight = isLandscape
    ? 0
    : getPrimaryNavigationBarHeight(insets.bottom, { androidUsesButtonNavBar });
  const surfaceClassName = getPrimaryNavigationStripSurfaceClassName(
    usePrimaryNavigationStripSurfaceColorVariable(),
  );
  // The page of an introduction the sheet is on. Another introduction starts on its first page,
  // and so does one the learner comes back to (the sheet leaves with them, and remounts).
  const [introductionPage, setIntroductionPage] = useState<{
    index: number;
    reaction: OnboardingPageReaction;
  } | null>(null);

  let content: OnboardingTextPage;
  let contentKey: string;
  let action: ReactNode;
  if (subject.kind === "reaction") {
    const { reaction } = subject;
    const pages = ONBOARDING_INTRODUCTIONS[reaction];
    const index = introductionPage?.reaction === reaction ? introductionPage.index : 0;
    const page = pages[index] ?? pages[0];
    const hasNextPage = index + 1 < pages.length;
    content = page;
    contentKey = page.id;
    let onPress: () => void;
    if (hasNextPage) {
      onPress = () => setIntroductionPage({ index: index + 1, reaction });
    } else if (reaction === "decks") {
      // The last stop: back to the cards the learner left for Stats.
      onPress = () => {
        playOnboardingReaction(reaction);
        switchTab("learn");
      };
    } else {
      onPress = () => playOnboardingReaction(reaction);
    }
    action = (
      <OnboardingContinueButton
        icon={page.icon}
        label={page.button}
        onPress={onPress}
        testID={`onboarding-sheet-${page.id}-continue-button`}
      />
    );
  } else {
    const announcement = ONBOARDING_ANNOUNCEMENTS[subject.destination];
    content = announcement;
    contentKey = `open-${subject.destination}`;
    action = (
      <OnboardingContinueButton
        icon={announcement.icon}
        label={announcement.button}
        onPress={() => switchTab(subject.destination)}
        testID={`onboarding-sheet-open-${subject.destination}-button`}
      />
    );
  }

  return (
    <Animated.View
      className={isLandscape ? "absolute bottom-0 z-40" : "absolute inset-x-0 z-40"}
      exiting={SlideOutDown.duration(350)}
      pointerEvents="box-none"
      style={[
        isLandscape
          ? {
              left: (width - Math.min(width, LANDSCAPE_SHEET_MAX_WIDTH)) / 2,
              width: Math.min(width, LANDSCAPE_SHEET_MAX_WIDTH),
            }
          : { bottom: stripHeight - STRIP_FADE_GUTTER_HEIGHT },
        entranceStyle,
      ]}
      testID="onboarding-sheet"
    >
      <View className={isLandscape ? "" : SHEET_SHADOW_ROOM_CLASS_NAME}>
        <View
          className={`${SHEET_SURFACE_CLASS_NAME} ${surfaceClassName} ${isLandscape ? "" : "pb-4"}`}
          style={isLandscape ? { paddingBottom: insets.bottom + 16 } : undefined}
        >
          <OnboardingSpeechBubble testID="onboarding-sheet-speech-bubble">
            <Animated.View
              className="gap-2"
              entering={FadeIn.duration(350)}
              exiting={FadeOut.duration(150)}
              key={contentKey}
            >
              <Text accessibilityRole="header" className="text-2xl font-bold text-foreground">
                {content.title}
              </Text>
              {content.paragraphs.map((paragraph) => (
                <Paragraph key={paragraph}>{paragraph}</Paragraph>
              ))}
            </Animated.View>
          </OnboardingSpeechBubble>
          <View className="mt-5">{action}</View>
        </View>
      </View>
      {isLandscape ? null : (
        // Covers the strip's gutter in the sheet's colour, and over its shadow, without taking the
        // touches meant for the strip's buttons.
        <View
          className={surfaceClassName}
          pointerEvents="none"
          style={{ height: STRIP_FADE_GUTTER_HEIGHT }}
        />
      )}
      <HaniSheetPerch
        compact={isLandscape}
        onReady={handleHaniReady}
        progress={progress}
        sheetTop={isLandscape ? 0 : 20}
        sheetWidth={isLandscape ? Math.min(width, LANDSCAPE_SHEET_MAX_WIDTH) : width}
      />
    </Animated.View>
  );
}

function Paragraph({ children }: { children: ReactNode }) {
  return <Text className="text-base leading-relaxed text-muted-foreground">{children}</Text>;
}
